import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NgOptimizedImage } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ShoppingBag, type LucideIconData } from 'lucide-angular';
import { Observable, catchError, map, of, startWith, switchMap, tap } from 'rxjs';

import { AuthService } from '../../../../shared/services/auth.service';
import { BadgeComponent, ButtonComponent, IconComponent, PaginationComponent } from '../../../../shared/ui';
import { formatCrc } from '../../../../shared/utils/currency.util';
import type { ProfileOrdersPage, ProfilePrepurchase } from '../../data/profile-orders.models';
import { ProfileOrdersService } from '../../data/profile-orders.service';
import { PROFILE_COPY } from '../../profile.copy';
import { formatIsoDate } from '../../utils/profile-format.util';
import { itemsCountLabel, orderStatusVariant, ordersBlockedByVerification } from '../../utils/profile-orders.util';
import { ProfileOrdersUnverifiedComponent } from '../profile-orders-unverified/profile-orders-unverified.component';
import { ProfilePrepurchasesComponent } from '../profile-prepurchases/profile-prepurchases.component';

type ListState =
  | { status: 'signed-out' }
  | { status: 'blocked' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; page: ProfileOrdersPage };

/** Lo que decide qué se pide. Cambia la cuenta, su verificación, la página o un reintento → petición nueva. */
interface ListKey {
  userId: number | null;
  blocked: boolean;
  page: number;
  attempt: number;
}

const sameKey = (a: ListKey, b: ListKey): boolean =>
  a.userId === b.userId && a.blocked === b.blocked && a.page === b.page && a.attempt === b.attempt;

/** «Precompras en curso»: la misma clave sin `page`. Cambiar de página no vuelve a pedir; reintentar, sí. */
type PrepurchasesKey = Omit<ListKey, 'page'>;

const samePrepurchasesKey = (a: PrepurchasesKey, b: PrepurchasesKey): boolean =>
  a.userId === b.userId && a.blocked === b.blocked && a.attempt === b.attempt;

/**
 * `?page=` en decimal puro (`^[1-9]\d*$`); cualquier otra cosa es la primera página. `Number()` a secas aceptaba
 * `0x10`, `1e3` o ` 7` y pedía la 16, la 1.000 o la 7 (Astra W1).
 */
const DECIMAL_PAGE = /^[1-9]\d*$/;

function pageFromQuery(raw: string | null): number {
  return raw !== null && DECIMAL_PAGE.test(raw) ? Number(raw) : 1;
}

/**
 * «Mis pedidos» (`/perfil/pedidos`, W1, §5.6.3 de B10): tarjeta por pedido, paginación en `?page=` y los estados de
 * carga, error, vacío y cuenta sin verificar (B44).
 *
 * **El estado pertenece a la cuenta, no al componente.** Todo sale de una clave con `currentUser()?.id`: si la sesión
 * cae, se vacía; si cambia el id (A → B, con o sin `null` en medio), se vuelve a pedir; y el `switchMap` cancela la
 * petición en vuelo de la cuenta anterior, así que una respuesta de A nunca se pinta con B dentro.
 *
 * Encima del listado, «Precompras en curso» (W1b, A2b) con su propia clave (sin `page`): solo en la página 1, solo si
 * hay alguna, y un fallo suyo no toca el listado.
 */
@Component({
  selector: 'app-profile-orders',
  imports: [
    BadgeComponent,
    ButtonComponent,
    IconComponent,
    NgOptimizedImage,
    PaginationComponent,
    ProfileOrdersUnverifiedComponent,
    ProfilePrepurchasesComponent,
    RouterLink,
  ],
  templateUrl: './profile-orders.component.html',
  styleUrl: './profile-orders.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileOrdersComponent {
  private readonly orders = inject(ProfileOrdersService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentUser = inject(AuthService).currentUser;

  protected readonly copy = PROFILE_COPY.ORDERS;
  protected readonly shoppingBagIcon: LucideIconData = ShoppingBag;
  protected readonly formatCrc = formatCrc;
  protected readonly formatIsoDate = formatIsoDate;
  protected readonly statusVariant = orderStatusVariant;
  protected readonly itemsLabel = itemsCountLabel;

  private readonly page = toSignal(this.route.queryParamMap.pipe(map((q) => pageFromQuery(q.get('page')))), {
    initialValue: 1,
  });
  private readonly attempt = signal(0);

  private readonly key = computed<ListKey>(
    () => ({
      userId: this.currentUser()?.id ?? null,
      blocked: ordersBlockedByVerification(this.currentUser()),
      page: this.page(),
      attempt: this.attempt(),
    }),
    { equal: sameKey },
  );

  protected readonly state = toSignal(toObservable(this.key).pipe(switchMap((key) => this.load(key))), {
    initialValue: { status: 'loading' } as ListState,
  });

  protected readonly loaded = computed(() => {
    const state = this.state();
    return state.status === 'loaded' ? state.page : null;
  });

  private readonly prepurchasesKey = computed<PrepurchasesKey>(
    () => ({
      userId: this.currentUser()?.id ?? null,
      blocked: ordersBlockedByVerification(this.currentUser()),
      attempt: this.attempt(),
    }),
    { equal: samePrepurchasesKey },
  );

  private readonly prepurchases = toSignal(
    toObservable(this.prepurchasesKey).pipe(switchMap((key) => this.loadPrepurchases(key))),
    { initialValue: [] as ProfilePrepurchase[] },
  );

  /** El bloque existe solo en la página 1 y solo si hay alguna: ni esqueleto ni título suelto. */
  protected readonly visiblePrepurchases = computed(() =>
    this.page() === 1 && this.prepurchases().length > 0 ? this.prepurchases() : null,
  );

  protected retry(): void {
    this.attempt.update((n) => n + 1);
  }

  protected onPageChange(page: number): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: page > 1 ? page : null },
      queryParamsHandling: 'merge',
    });
  }

  /**
   * Página más allá de la última (`data: []` con `total > 0`; p. ej. `?page=99` que sobrevive a un cambio de
   * cuenta): se corrige a la última con `replaceUrl`. Una sola corrección por respuesta y solo hacia atrás, así
   * que no hay bucle aunque la última también llegara vacía.
   */
  private fixOutOfRange(requested: number, result: ProfileOrdersPage): void {
    const last = Math.max(1, result.meta.total_pages);
    if (result.data.length > 0 || result.meta.total === 0 || requested <= last) return;
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { page: last > 1 ? last : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private load(key: ListKey): Observable<ListState> {
    if (key.userId === null) return of({ status: 'signed-out' });
    if (key.blocked) return of({ status: 'blocked' });
    return this.orders.list(key.page).pipe(
      tap((page) => this.fixOutOfRange(key.page, page)),
      map((page): ListState => ({ status: 'loaded', page })),
      catchError(() => of<ListState>({ status: 'error' })),
      startWith<ListState>({ status: 'loading' }),
    );
  }

  /**
   * Sin cuenta o sin verificar (B44) no se pide. Mientras carga o tras un error, `[]`: el bloque no existe y el
   * listado sigue a lo suyo. El error no se traga en silencio: `fail()` ya lo ha tratado (401 → `me()`) y aquí se
   * deja la traza de qué pantalla se quedó sin datos.
   */
  private loadPrepurchases(key: PrepurchasesKey): Observable<ProfilePrepurchase[]> {
    if (key.userId === null || key.blocked) return of([]);
    return this.orders.prepurchases().pipe(
      catchError((failure: unknown) => {
        console.error('[profile] ✗ precompras en curso', failure);
        return of<ProfilePrepurchase[]>([]);
      }),
      startWith<ProfilePrepurchase[]>([]),
    );
  }
}
